import React, { ReactNode, useState, Suspense, lazy } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useAdmin } from '../../contexts/AdminContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import MobileBottomNav from './MobileBottomNav';
import AppLoadingScreen from '../shared/AppLoadingScreen';
import ImpersonationBanner from '../shared/ImpersonationBanner';
import AccountNoticeBar from '../shared/AccountNoticeBar';
import { OnboardingWizard } from '../onboarding/OnboardingWizard';
import MobileMenuDrawer from './MobileMenuDrawer';
import NotificationToastBridge from '../notifications/NotificationToastBridge';

const DownloadAppPage = lazy(() => import('../../pages/DownloadAppPage'));

const isMobilePhone = () =>
  /Android|iPhone|iPod/i.test(navigator.userAgent);

interface AuthenticatedLayoutProps {
  children: ReactNode;
}

const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: configLoading, error: configError, config, refreshConfig } = useAppConfig();
  const { isHostAdmin, isPartner, isLoading: adminLoading } = useAdmin();
  const { isModalOpen } = useOnboarding();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileBypass, setMobileBypass] = useState(() =>
    sessionStorage.getItem('mobileBypass') === '1'
  );

  // Check auth before rendering any layout
  if (authLoading || configLoading || adminLoading) {
    return <AppLoadingScreen />;
  }

  // If the app config API failed and there is no cached config, show an error screen
  if (configError && !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center max-w-sm mx-auto p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-ink-900 mb-2">Unable to connect</h2>
          <p className="text-sm text-ink-500 mb-6">
            The application could not reach the server. Please check your connection and try again.
          </p>
          <button
            onClick={() => refreshConfig(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect host-level users (host admins AND partners) to the admin area.
  // Both have no tenant context and must never render the tenant main site.
  if (isHostAdmin || isPartner) {
    return <Navigate to="/admin" replace />;
  }

  if (isMobilePhone() && !mobileBypass) {
    return (
      <Suspense fallback={<AppLoadingScreen />}>
        <DownloadAppPage onSkip={() => {
          sessionStorage.setItem('mobileBypass', '1');
          setMobileBypass(true);
        }} />
      </Suspense>
    );
  }

  // Only render layout if authenticated and not a host admin
  return (
    <div className="h-screen flex flex-col bg-ink-50/50">
      <NotificationToastBridge />
      {/* Impersonation banner sits as a flex-none row at the top.
          Renders nothing when not impersonating so the layout below
          fills the full viewport. When impersonating it takes its own
          row — no document-level overflow / outer scrollbar. */}
      <ImpersonationBanner />

      <div className="flex-1 min-h-0 flex">
        {/* Desktop Sidebar — full height of the remaining flex row */}
        <Sidebar className="flex-none h-full sticky top-0" />

        {/* Right side: Navbar + Content */}
        <div className="flex-1 flex flex-col min-w-0">
        {/* Single account-notice strip — setup session offer and/or add-a-card nudge.
            Renders nothing when neither applies. */}
        <AccountNoticeBar />

        {/* Top Navigation */}
        <Navbar 
          className="flex-none sticky top-0 z-40" 
          onMobileMenuOpen={() => setShowMobileMenu(true)}
        />
        
        {/* Main Content */}
        <main className="flex-1 relative overflow-hidden">
          <div className="absolute inset-0 overflow-y-auto pb-20 md:pb-0">
            <Suspense fallback={<AppLoadingScreen inline />}>
              {children}
            </Suspense>
          </div>
        </main>
        </div>
      </div>

      {/* Mobile Bottom Navigation - Only on mobile */}
      <MobileBottomNav onMoreClick={() => setShowMobileMenu(true)} />
      
      {/* Mobile Menu Drawer */}
      <MobileMenuDrawer 
        isOpen={showMobileMenu} 
        onClose={() => setShowMobileMenu(false)} 
      />
      
      {/* Onboarding Modal Overlay */}
      {isModalOpen && <OnboardingWizard />}
    </div>
  );
};

export default AuthenticatedLayout;
